import React from 'react';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  isActive?: boolean;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  onNavigate?: (path: string) => void;
  showSearch?: boolean;
  onSearchClick?: () => void;
  showHistory?: boolean;
  onHistoryClick?: () => void;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  onNavigate,
  showSearch = false,
  onSearchClick,
  showHistory = false,
  onHistoryClick,
}) => {
  const handleClick = (event: React.MouseEvent, path: string) => {
    event.preventDefault();
    if (onNavigate) {
      onNavigate(path);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      mb: 2,
      p: 2,
      backgroundColor: '#f8f9fa',
      borderRadius: 2,
      border: '1px solid #e9ecef'
    }}>
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#6c757d' }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
        }}
      >
        {items.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
            {item.icon && (
              <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </Box>
            )}
            {item.isActive ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#495057',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {item.label}
              </Typography>
            ) : item.path ? (
              <Link
                component="button"
                variant="body2"
                onClick={(e) => handleClick(e, item.path!)}
                sx={{
                  color: '#007bff',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#0056b3',
                  },
                }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: '#6c757d',
                  fontSize: '0.875rem',
                }}
              >
                {item.label}
              </Typography>
            )}
          </Box>
        ))}
      </MuiBreadcrumbs>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {showSearch && (
          <Tooltip title="Поиск по разделам">
            <IconButton
              size="small"
              onClick={onSearchClick}
              sx={{
                color: '#007bff',
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 255, 0.1)',
                },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {showHistory && (
          <Tooltip title="История просмотров">
            <IconButton
              size="small"
              onClick={onHistoryClick}
              sx={{
                color: '#6c757d',
                '&:hover': {
                  backgroundColor: 'rgba(108, 117, 125, 0.1)',
                },
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default Breadcrumbs;
